import * as React from 'react';
import styled from 'styled-components';
import { ReactNode } from 'react';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { mergeTests } from '../../test-utilities/mergeTests';
import { FlexRow } from '../FlexRow';

export const FormCellErrorText = styled.div`
  ${(props) => props.theme.fonts.mixins.tiny};
  color: ${(props) => props.theme.colors.errorText};
`;

export const FormCellHintText = styled.div<{ disabled?: boolean; paddingLeftSize?: string }>`
  ${(props) => props.theme.fonts.mixins.tiny};
  color: ${(props) => (props.disabled ? props.theme.colors.gray : props.theme.colors.gray500)};
  padding-left: ${(props) => props.paddingLeftSize};
`;

interface IFormCellLabelProps {
  removeBottomMargin: boolean;
  isError: boolean;
}

export const FormCellLabel = styled.div<IFormCellLabelProps>`
  ${(props) => props.theme.fonts.mixins.tiny};
  color: ${(props) => (props.isError ? props.theme.colors.errorText : props.theme.colors.defaultContentText)};
  ${(props) => (props.removeBottomMargin ? '' : `margin-bottom:${props.theme.spacing.spacing4};`)}
`;

const StyledFormCell = styled.div<IFormCellProps>`
  position: relative;
  display: grid;
  grid-auto-rows: min-content;
`;

export interface IFormCellProps extends IHasTestData {
  /** Additional CSS class. */
  className?: string;
  /** Message to display when the input is in an error state. When this is present, also visually highlights the component as in error. */
  errorText?: ReactNode;
  /** Hint text content. */
  hintText?: ReactNode;
  /** Highlights the input as a required field (does also perform additional validation). */
  isRequired?: boolean;
  /** This label appears above the input. */
  label?: ReactNode;
  /** @Deprecated Update your UI so it does not use this prop */
  wrapInput?: boolean;

  disabled?: boolean;

  paddingLeftSize?: string;
  /** This can be used to pass action/icon */
  action?: ReactNode;
  /** This adds a character count to upper right of label */
  maxCharacterCount?: number;
  currentCharacterCount?: number;
  children?: ReactNode;
}

/**
 * This component wraps a input with proper spacing, along with support for a label, hint text, and validation state.
 */
export const FormCell: React.FC<IFormCellProps> = ({
  children,
  errorText,
  hintText,
  isRequired,
  label,
  wrapInput = true,
  className,
  disabled,
  paddingLeftSize,
  action,
  maxCharacterCount,
  currentCharacterCount,
  ...props
}) => {
  return (
    <StyledFormCell
      className={className}
      data-test-id={mergeTests(props['data-test-id'], 'form-cell')}
      data-test-class={mergeTests(props['data-test-class'], 'form-cell')}
    >
      {label && (
        <FormCellLabel
          isError={!!errorText}
          removeBottomMargin={wrapInput}
          data-test-id={mergeTests(props['data-test-id'], 'form-cell', 'label')}
          data-test-class={mergeTests(props['data-test-class'], 'form-cell', 'label')}
        >
          <FlexRow justifyContent={'space-between'}>
            <div>
              {label}
              {isRequired ? ' *' : ''}
            </div>
            {maxCharacterCount && (
              <div className={!!errorText ? undefined : 'text-[#596B82]'}>
                {currentCharacterCount} / {maxCharacterCount}
              </div>
            )}
            {action}
          </FlexRow>
        </FormCellLabel>
      )}
      {children}
      {errorText && (
        <FormCellErrorText
          data-class={'form-cell-error'}
          data-test-id={mergeTests(props['data-test-id'], 'form-cell', 'error')}
          data-test-class={mergeTests(props['data-test-class'], 'form-cell', 'error')}
        >
          {errorText}
        </FormCellErrorText>
      )}

      {hintText && (
        <FormCellHintText
          data-test-id={mergeTests(props['data-test-id'], 'form-cell', 'hint')}
          data-test-class={mergeTests(props['data-test-class'], 'form-cell', 'hint')}
          disabled={!!disabled}
          paddingLeftSize={paddingLeftSize}
        >
          {hintText}
        </FormCellHintText>
      )}
    </StyledFormCell>
  );
};
